import { useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import type { QueryObserverResult } from '@tanstack/react-query'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type { ClaimChatsResponse } from 'tripPlanner/api/types'
import { getTripPlannerVisitorId } from 'tripPlanner/utils/visitorIdentity'

type UseAnonymousChatClaimingArgs = {
  isLoggedIn: boolean
  claimChatsMutation: (args: { visitorId: string }) => Promise<ClaimChatsResponse>
  refetchChatList: () => Promise<QueryObserverResult>
}

export function useAnonymousChatClaiming({
  isLoggedIn,
  claimChatsMutation,
  refetchChatList,
}: UseAnonymousChatClaimingArgs) {
  const intl = useIntl()
  const hasClaimedChatsRef = useRef(false)

  useEffect(() => {
    if (!isLoggedIn) {
      hasClaimedChatsRef.current = false
      return
    }
    if (hasClaimedChatsRef.current) {
      return
    }
    const visitorId = getTripPlannerVisitorId()
    if (!visitorId) {
      hasClaimedChatsRef.current = true
      return
    }

    claimChatsMutation({ visitorId })
      .then(() => refetchChatList())
      .catch((error) => {
        console.error('[TripChat] Failed to claim chats for logged in user', error)
        showSnackbar(intl.formatMessage({
          id: 'dxaUBDAJdE579J2WiwME',
          defaultMessage: 'Your previous chat could not be linked to your account. Try signing out and back in.',
          description: 'Snackbar error message when claiming anonymous chat history to a logged-in account fails',
        }), 'critical')
      })
      .finally(() => {
        hasClaimedChatsRef.current = true
      })
  }, [isLoggedIn, claimChatsMutation, refetchChatList, intl])
}
